import { useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import { QRCodeSVG } from 'qrcode.react';

import { SimpleModalContentStyled } from 'lib/modal/simple-modal.styles';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import { useRegionData } from 'approot/shared/region/region.apollo';
import AssignmentsFeedback from 'approot/shared/assignments-feedback/assignments-feedback';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import type { GetClassStudentsQuery } from '../../students/students.graphql.generated';
import {
  INTERCOM_GOOGLE_SIGNIN_HELP_LINK_US,
  INTERCOM_GOOGLE_SIGNIN_HELP_LINK_AU,
} from 'global/constants';
import Tabs from 'approot/shared/tabs/tabs';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  BoldStyled,
  Heading18,
  OlStyled,
  SmallStyled,
} from 'approot/shared/styles/typography.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import {
  SigninTypeCEnet,
  SigninTypeClassCode,
  SigninTypeEmail,
  SigninTypeGoogle,
  SigninTypeNSWDoe,
  SigninTypeQrCode,
} from '../signin/signin-type/signin-type';
import {
  AssignClassOr,
  AssignClassActions,
  AssignClassDetail,
  AssignClassBox,
} from './assign-class-detail-modal.style';
import {
  ListInquisitiveLink,
  ListStudentSignIn,
} from './assign-class-details-shared-list';
import * as Styled from './assign-class-details-authenticated.style';
import * as UnauthStyled from './assign-class-details-unauthenticated.style';
import AssignPrintOptions from './assign-print-options';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import {
  NotificationTip,
  NotificationTipText,
} from 'approot/shared/notifications/notification-tip.style';
import {
  getStudentSigninTabOptions,
  SignInType,
  TAB_OPTIONS,
} from 'approot/shared/assign/get-student-signin-tab-options';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  students: Pluck<GetClassStudentsQuery, 'getClassStudents'>[];
};

const AssignClassDetailsAuthenticated = ({ students }: Props) => {
  const currentClass = useCurrentClassVar();
  const classQRCodeUrl = currentClass?.classToDoQRCodeUrl || '';
  const classPassCode = currentClass?.classToDoPasscode?.toString();
  const classToDoId = currentClass?.classToDoId;
  const { isRegionAU } = useRegionData();
  const { registerHeading } = useAccessibleHeading();

  const sortedStudents = useMemo(
    () => orderBy(students, 'email', 'asc'),
    [students]
  );

  const allStudentsHaveUsernames = useMemo(
    () => students.every(s => s.username),
    [students]
  );

  const [tab, setSelectedTab] = useState<string>(
    allStudentsHaveUsernames
      ? TAB_OPTIONS[SignInType.Username].value
      : TAB_OPTIONS[SignInType.EmailOrUsername].value
  );
  const options = useMemo(() => {
    return getStudentSigninTabOptions({
      allStudentsHaveUsernames,
      isRegionAU,
    });
  }, [allStudentsHaveUsernames, isRegionAU]);

  return (
    <>
      <ScreenReaderOnly as="h1" tabIndex={-1} ref={registerHeading}>
        How your students sign in
      </ScreenReaderOnly>

      <Styled.AssignClassDetailsTabs>
        <Tabs
          fillWidth={false}
          options={options}
          activeTab={tab}
          ariaDescription="How Students Sign In Options"
          contentPanelId="tab-content"
          onSelect={tab => setSelectedTab(tab.value as string)}
        />
      </Styled.AssignClassDetailsTabs>

      <SimpleModalContentStyled id="tab-content">
        {tab === TAB_OPTIONS[SignInType.ClassCode].value && (
          <NotificationTip>
            <NotificationTipText marginBottom={0}>
              Students signed in with a class code can view content but can’t
              respond. To collect student responses, use another sign-in method.
            </NotificationTipText>
          </NotificationTip>
        )}
        <AssignClassBox>
          <AssignClassDetail>
            <Box $alignItems="center" $gap={8} $marginBottom={16}>
              <SigninTypeQrCode />
              <div>
                <Styled.AssignClassSmall>
                  If you have a sign in card
                </Styled.AssignClassSmall>
                <Heading18 as="h2" marginBottom={0}>
                  Sign in with QR Code
                </Heading18>
              </div>
            </Box>

            <OlStyled>
              <ListInquisitiveLink />
              <ListStudentSignIn />
              {tab !== TAB_OPTIONS[SignInType.ClassCode].value ? (
                <li>
                  Choose <strong>QR Code</strong> and scan the qr code{' '}
                  <strong>on your sign in card</strong>.
                </li>
              ) : (
                <SignInWithQRCode classQRCodeUrl={classQRCodeUrl} />
              )}
            </OlStyled>
          </AssignClassDetail>
          <AssignClassOr>
            <span>OR</span>
          </AssignClassOr>
          {tab === TAB_OPTIONS[SignInType.Username].value && (
            <SignInWithUsername />
          )}
          {tab === TAB_OPTIONS[SignInType.EmailOrUsername].value && (
            <SignInWithEmailOrUsername />
          )}
          {tab === TAB_OPTIONS[SignInType.Google].value && <SignInWithGoogle />}
          {tab === TAB_OPTIONS[SignInType.NSWDoE].value && <SignInWithNSWDoE />}
          {tab === TAB_OPTIONS[SignInType.CEnet].value && <SignInWithCEnet />}
          {tab === TAB_OPTIONS[SignInType.ClassCode].value && (
            <SignInWithClassCode
              classPassCode={classPassCode}
              classToDoId={classToDoId}
            />
          )}
        </AssignClassBox>

        <HrStyled $marginTop={0} $marginBottom={24} />

        <AssignClassActions>
          <AssignmentsFeedback buttonText="Give us Feedback" isButton />
          <AssignPrintOptions students={sortedStudents} />
        </AssignClassActions>
      </SimpleModalContentStyled>
    </>
  );
};

export default AssignClassDetailsAuthenticated;

const SignInWithUsername = () => {
  return (
    <AssignClassDetail>
      <Box $alignItems="center" $gap={8} $marginBottom={16}>
        <SigninTypeEmail />
        <Heading18 as="h2" marginBottom={0}>
          Sign in with Username
        </Heading18>
      </Box>
      <OlStyled>
        <ListInquisitiveLink />
        <ListStudentSignIn />
        <li>
          Choose <strong>Email or Username</strong> and enter the username and
          password <strong>on your sign in card</strong>.
        </li>
      </OlStyled>
    </AssignClassDetail>
  );
};

const SignInWithEmailOrUsername = () => {
  return (
    <AssignClassDetail>
      <Box $alignItems="center" $gap={8} $marginBottom={16}>
        <SigninTypeEmail />
        <Heading18 as="h2" marginBottom={0}>
          Sign in with Email or Username
        </Heading18>
      </Box>
      <OlStyled>
        <ListInquisitiveLink />
        <ListStudentSignIn />
        <li>
          Choose <strong>Email or Username</strong> and enter your email or
          username and password <strong>on your sign in card</strong>.
        </li>
      </OlStyled>
    </AssignClassDetail>
  );
};

const SignInWithGoogle = () => {
  const { isRegionAU } = useRegionData();

  return (
    <AssignClassDetail>
      <Box $alignItems="center" $gap={8} $marginBottom={16}>
        <SigninTypeGoogle />
        <Heading18 as="h2" marginBottom={0}>
          Sign in with Google
        </Heading18>
      </Box>
      <OlStyled>
        <ListInquisitiveLink />
        <ListStudentSignIn />
        <li>
          Choose <strong>Google</strong> and enter your email and password.
        </li>
      </OlStyled>
      <SmallStyled as="p" marginBottom={0}>
        Is your student’s access blocked?
      </SmallStyled>
      <Styled.AssignClassSmallLink
        href={
          isRegionAU
            ? INTERCOM_GOOGLE_SIGNIN_HELP_LINK_AU
            : INTERCOM_GOOGLE_SIGNIN_HELP_LINK_US
        }
        target="_blank"
        rel="noreferrer noopener"
      >
        Click here for help
      </Styled.AssignClassSmallLink>
    </AssignClassDetail>
  );
};

const SignInWithNSWDoE = () => {
  return (
    <AssignClassDetail>
      <Box $alignItems="center" $gap={8} $marginBottom={16}>
        <SigninTypeNSWDoe />
        <Heading18 as="h2" marginBottom={0}>
          Sign in with NSW DoE
        </Heading18>
      </Box>
      <OlStyled>
        <ListInquisitiveLink />
        <ListStudentSignIn />
        <li>
          Choose <strong>NSW DoE</strong> and enter your email and password.
        </li>
      </OlStyled>
    </AssignClassDetail>
  );
};

const SignInWithCEnet = () => {
  return (
    <AssignClassDetail>
      <Box $alignItems="center" $gap={8} $marginBottom={16}>
        <SigninTypeCEnet />
        <Heading18 as="h2" marginBottom={0}>
          Sign in with CEnet
        </Heading18>
      </Box>
      <OlStyled>
        <ListInquisitiveLink />
        <ListStudentSignIn />
        <li>
          Choose <strong>CEnet</strong> and enter your email and password.
        </li>
      </OlStyled>
    </AssignClassDetail>
  );
};

const SignInWithQRCode = ({ classQRCodeUrl }: { classQRCodeUrl: string }) => {
  return (
    <>
      <li>
        Choose <strong>QR Code</strong> and scan the qr code{' '}
        <strong>below:</strong>.
      </li>
      <UnauthStyled.AssignQrContainer>
        <QRCodeSVG value={classQRCodeUrl} size={160} />
      </UnauthStyled.AssignQrContainer>
    </>
  );
};

const SignInWithClassCode = ({
  classPassCode,
  classToDoId,
}: {
  classPassCode?: string;
  classToDoId?: string;
}) => {
  return (
    <AssignClassDetail>
      <Box $alignItems="center" $gap={8} $marginBottom={16}>
        <SigninTypeClassCode />
        <Heading18 as="h2" marginBottom={0}>
          Sign in with Class Code
        </Heading18>
      </Box>
      <OlStyled>
        <ListInquisitiveLink />
        <ListStudentSignIn />
        <li>
          Choose <strong>Class Code</strong> and enter the details below:
        </li>
      </OlStyled>
      <UnauthStyled.AssignClassCodes>
        <UnauthStyled.AssignClassCodesLabel>
          Class Code
        </UnauthStyled.AssignClassCodesLabel>
        <BoldStyled marginBottom={0} data-selector="class-code">
          {classToDoId}
        </BoldStyled>
      </UnauthStyled.AssignClassCodes>
      <UnauthStyled.AssignClassCodes>
        <UnauthStyled.AssignClassCodesLabel>
          Access Code
        </UnauthStyled.AssignClassCodesLabel>
        <BoldStyled marginBottom={0} data-selector="access-code">
          {classPassCode}
        </BoldStyled>
      </UnauthStyled.AssignClassCodes>
    </AssignClassDetail>
  );
};
